// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductActions from "@turnerBlueprint/App/Components/Product/ProductActions";
import { TurnerProductInfo } from "@turnerBlueprint/App/Services/TurnerApiModels";
import { ProductAvailabilityStyles } from "@turnerBlueprint/Overrides/Components/ProductAvailability";
import React, { FC } from "react";

type Props = HasProductContext;

export interface ProductDetailsSummaryStyles {
    container?: InjectableCss;
    rightSection?: InjectableCss;
    leftSection?: InjectableCss;
    productImage?: LazyImageProps;
    productTitle?: TypographyPresentationProps;
    brandStyles?: ProductBrandStyles;
    titleWrapper?: InjectableCss;
    numbersAvailablityWrapper?: InjectableCss;
    numbersWrapper?: InjectableCss;
    valueText?: TypographyPresentationProps;
    labelText?: TypographyPresentationProps;
    availability?: ProductAvailabilityStyles;
    productPrice?: ProductPriceStyles;
}

export const productDetailsSummaryStyles: ProductDetailsSummaryStyles = {};

const styles = productDetailsSummaryStyles;

const ProductDetailsSummary: FC<Props> = ({ productContext }) => {
    const { product } = productContext;
    const { configurationCompleted, productSettings } = useAppSelector(state => ({
        configurationCompleted: state.pages.productDetails.configurationCompleted,
        productSettings: getSettingsCollection(state).productSettings,
    }));
    if (!product) {
        return null;
    }

    const productImage = product.smallImagePath || product.mediumImagePath || product.largeImagePath;

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftSection}>
                <LazyImage
                    src={productImage}
                    altText={product.imageAltText || product.productTitle}
                    {...styles.productImage}
                />
                <StyledWrapper {...styles.titleWrapper}>
                    {product.brand && (
                        <ProductBrand brand={product.brand} showLogo={false} extendedStyles={styles.brandStyles} />
                    )}
                    <Typography {...styles.productTitle}>{product.productTitle}</Typography>
                    <StyledWrapper {...styles.numbersAvailablityWrapper}>
                        <StyledWrapper {...styles.numbersWrapper}>
                            {productContext.product.customerProductNumber ? (
                                <Typography {...styles.valueText} as="p" data-test-selector="customerProductNumber">
                                    <Typography {...styles.labelText}>{translate("My Part #")}:</Typography>
                                    {productContext.product.customerProductNumber}
                                </Typography>
                            ) : productContext.product.productNumber ? (
                                <Typography {...styles.valueText} as="p" data-test-selector="productNumber">
                                    <Typography {...styles.labelText}>{translate("Part #")}:</Typography>
                                    {productContext.product.productNumber}
                                </Typography>
                            ) : (
                                <></>
                            )}
                            {!productContext!.product!.isVariantParent &&
                                (productContext.productInfo as TurnerProductInfo)!.manufacturerItem && (
                                    <Typography {...styles.valueText}>
                                        <Typography {...styles.labelText}>{translate("MFG #")}:</Typography>
                                        {(productContext.productInfo as TurnerProductInfo)!.manufacturerItem}
                                    </Typography>
                                )}
                            {productContext!.product !== undefined &&
                                (productContext!.product!.isVariantParent ||
                                    productContext!.product!.variantTraits === undefined ||
                                    productContext!.product!.variantTraits!.length < 1) &&
                                productContext!.product!.manufacturerItem && (
                                    <Typography {...styles.valueText}>
                                        <Typography {...styles.labelText}>{translate("MFG #")}:</Typography>
                                        {productContext!.product!.manufacturerItem}
                                    </Typography>
                                )}
                        </StyledWrapper>
                        <StyledWrapper>
                            <ProductContextAvailability
                                isProductDetailsPage={true}
                                configurationCompleted={configurationCompleted}
                                extendedStyles={styles.availability}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.rightSection}>
                {!productContext.product.isVariantParent && (
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.productPrice}
                        showPack={false}
                    />
                )}
                <ProductActions
                    isParent={product.isVariantParent}
                    isPdp={true}
                    showQty={true}
                    hideUOM={false}
                    showAddToList={true}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(ProductDetailsSummary);
